/**
 * File input image editor (standalone).
 *
 * Adds basic editing for input[type=file] image fields:
 * crop, rotate, flip and reset.
 *
 * Uses CropperJS via CDN and does not depend on Media Manager.
 *
 * @package Labead
 */

const CROPPER_CDN = {
	js: 'https://cdn.jsdelivr.net/npm/cropperjs@1/dist/cropper.min.js',
	css: 'https://cdn.jsdelivr.net/npm/cropperjs@1/dist/cropper.min.css',
};

let cropperReadyPromise = null;

function loadCropper() {
	if (typeof window.Cropper !== 'undefined') {
		return Promise.resolve();
	}

	if (cropperReadyPromise) {
		return cropperReadyPromise;
	}

	cropperReadyPromise = new Promise((resolve, reject) => {
		const existingCss = document.querySelector(`link[href="${CROPPER_CDN.css}"]`);
		if (!existingCss) {
			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.href = CROPPER_CDN.css;
			document.head.appendChild(link);
		}

		const existingJs = document.querySelector(`script[src="${CROPPER_CDN.js}"]`);
		if (existingJs) {
			existingJs.addEventListener('load', () => resolve(), { once: true });
			existingJs.addEventListener('error', () => reject(new Error('cropper-load-failed')), { once: true });
			if (typeof window.Cropper !== 'undefined') {
				resolve();
			}
			return;
		}

		const script = document.createElement('script');
		script.src = CROPPER_CDN.js;
		script.onload = () => resolve();
		script.onerror = () => reject(new Error('cropper-load-failed'));
		document.head.appendChild(script);
	});

	return cropperReadyPromise;
}

function isImageFile(file) {
	return !!file && typeof file.type === 'string' && file.type.startsWith('image/');
}

function preferredMimeType(file) {
	const supported = ['image/jpeg', 'image/png', 'image/webp'];
	return supported.includes(file.type) ? file.type : 'image/jpeg';
}

function setFieldState(input) {
	const wrapper = input.closest('[data-lb-file-editor]');
	if (!wrapper) return;

	const nameEl = wrapper.querySelector('[data-lb-file-name]');
	const editBtn = wrapper.querySelector('[data-lb-file-edit]');
	const emptyLabel = input.dataset.lbEmptyLabel || 'Nada selecionado';
	const editLabel = input.dataset.lbEditLabel || 'Editar imagem';
	const file = input.files && input.files.length ? input.files[0] : null;

	if (nameEl) {
		nameEl.textContent = file ? file.name : emptyLabel;
	}

	if (editBtn) {
		editBtn.textContent = editLabel;
		const canEdit = !!file && isImageFile(file) && (input.files?.length || 0) === 1 && !input.disabled;
		editBtn.classList.toggle('lw:hidden', !canEdit);
	}
}

function createEditorModal(file) {
	const url = URL.createObjectURL(file);
	const modal = document.createElement('div');
	modal.className = 'lw:fixed lw:inset-0 lw:z-[100010] lw:flex lw:items-center lw:justify-center';
	modal.innerHTML = `
		<div class="lw:absolute lw:inset-0 lw:bg-black/80" data-lb-close="1"></div>
		<div class="lw:relative lw:flex lw:flex-col lw:w-[96vw] lw:h-[96vh] lw:bg-theme-surface lw:text-theme-on-surface lw:rounded-xl lw:shadow-2xl lw:overflow-hidden">
			<div class="lw:flex lw:items-center lw:justify-between lw:px-4 lw:py-2.5 lw:border-b lw:border-theme-border lw:flex-shrink-0">
				<h5 class="lw:text-sm lw:font-semibold lw:m-0">Editar imagem</h5>
				<button type="button" class="lw:w-8 lw:h-8 lw:flex lw:items-center lw:justify-center lw:rounded lw:border-0 lw:bg-transparent lw:cursor-pointer lw:hover:opacity-85" data-lb-close="1">X</button>
			</div>

			<div class="lw:flex lw:flex-1 lw:overflow-hidden">
				<div class="lw:w-52 lw:flex-shrink-0 lw:border-r lw:border-theme-border lw:overflow-y-auto lw:p-3 lw:flex lw:flex-col lw:gap-3">
					<button type="button" class="button" data-lb-action="crop">Recortar</button>
					<div class="lw:grid lw:grid-cols-2 lw:gap-2">
						<button type="button" class="button" data-lb-action="rotate-left">-90</button>
						<button type="button" class="button" data-lb-action="rotate-right">+90</button>
						<button type="button" class="button" data-lb-action="flip-h">Espelhar na horizontal</button>
						<button type="button" class="button" data-lb-action="flip-v">Espelhar na vertical</button>
						<button type="button" class="button" data-lb-action="zoom-in">Zoom +</button>
						<button type="button" class="button" data-lb-action="zoom-out">Zoom -</button>
					</div>
					<button type="button" class="button" data-lb-action="reset">Desfazer tudo</button>
				</div>

				<div class="lw:flex-1 lw:bg-neutral-900 lw:flex lw:items-center lw:justify-center lw:overflow-hidden">
					<div class="lw:w-full lw:h-full">
						<img src="${url}" alt="" class="lw:max-w-full lw:max-h-full lw:block" data-lb-editor-image>
					</div>
				</div>
			</div>

			<div class="lw:flex lw:items-center lw:justify-end lw:gap-2 lw:px-4 lw:py-3 lw:border-t lw:border-theme-border lw:flex-shrink-0">
				<button type="button" class="button" data-lb-close="1">Cancelar</button>
				<button type="button" class="lw:bg-theme-accent lw:text-theme-on-accent lw:hover:opacity-90 lw:px-4 lw:py-2 lw:rounded" data-lb-apply="1">Aplicar</button>
			</div>
		</div>
	`;

	document.body.appendChild(modal);

	const image = modal.querySelector('[data-lb-editor-image]');
	const cropper = new window.Cropper(image, {
		viewMode: 1,
		dragMode: 'move',
		autoCrop: false,
		autoCropArea: 0.8,
		responsive: true,
		restore: false,
		guides: true,
		center: true,
		highlight: true,
		cropBoxMovable: true,
		cropBoxResizable: true,
		toggleDragModeOnDblclick: true,
		initialAspectRatio: NaN,
	});

	let scaleX = 1;
	let scaleY = 1;

	const close = () => {
		cropper.destroy();
		URL.revokeObjectURL(url);
		modal.remove();
		document.removeEventListener('keydown', onEsc);
	};

	const onEsc = (ev) => {
		if (ev.key === 'Escape') close();
	};
	document.addEventListener('keydown', onEsc);

	modal.querySelectorAll('[data-lb-close="1"]').forEach((btn) => {
		btn.addEventListener('click', close);
	});

	modal.querySelectorAll('[data-lb-action]').forEach((btn) => {
		btn.addEventListener('click', () => {
			switch (btn.dataset.lbAction) {
				case 'crop':
					cropper.crop();
					break;
				case 'rotate-left':
					cropper.rotate(-90);
					break;
				case 'rotate-right':
					cropper.rotate(90);
					break;
				case 'flip-h':
					scaleX *= -1;
					cropper.scaleX(scaleX);
					break;
				case 'flip-v':
					scaleY *= -1;
					cropper.scaleY(scaleY);
					break;
				case 'zoom-in':
					cropper.zoom(0.1);
					break;
				case 'zoom-out':
					cropper.zoom(-0.1);
					break;
				case 'reset':
					cropper.reset();
					scaleX = 1;
					scaleY = 1;
					break;
				default:
					break;
			}
		});
	});

	return {
		modal,
		cropper,
		close,
	};
}

async function applyEditedImage(input) {
	const file = input.files && input.files.length ? input.files[0] : null;
	if (!file) {
		window.lbAlert.toast({ color: 'w', message: 'Selecione uma imagem primeiro.' });
		return;
	}

	if (!isImageFile(file)) {
		window.lbAlert.toast({ color: 'w', message: 'A edição está disponível apenas para imagens.' });
		return;
	}

	if ((input.files?.length || 0) > 1) {
		window.lbAlert.toast({ color: 'w', message: 'Selecione apenas 1 imagem para editar.' });
		return;
	}

	try {
		await loadCropper();
	} catch {
		window.lbAlert.toast({ color: 'd', message: 'Não foi possível carregar o editor de imagem.' });
		return;
	}

	const editor = createEditorModal(file);
	const applyBtn = editor.modal.querySelector('[data-lb-apply="1"]');

	applyBtn.addEventListener('click', () => {
		const mimeType = preferredMimeType(file);
		const quality = mimeType === 'image/jpeg' ? 0.92 : undefined;
		const canvas = editor.cropper.getCroppedCanvas();

		canvas.toBlob((blob) => {
			if (!blob) {
				window.lbAlert.toast({ color: 'd', message: 'Falha ao processar a imagem.' });
				return;
			}

			const editedFile = new File([blob], file.name, {
				type: mimeType,
				lastModified: Date.now(),
			});

			const dt = new DataTransfer();
			dt.items.add(editedFile);
			input.files = dt.files;
			input.dispatchEvent(new Event('change', { bubbles: true }));

			editor.close();
			window.lbAlert.toast({ color: 's', message: 'Imagem editada com sucesso.' });
		}, mimeType, quality);
	});
}

function bindField(wrapper) {
	if (!wrapper || wrapper.dataset.lbFileEditorBound === '1') return;

	const input = wrapper.querySelector('[data-lb-file-input]');
	const editBtn = wrapper.querySelector('[data-lb-file-edit]');
	if (!input || !editBtn) return;

	wrapper.dataset.lbFileEditorBound = '1';

	input.addEventListener('change', () => setFieldState(input));
	editBtn.addEventListener('click', () => applyEditedImage(input));

	setFieldState(input);
}

export default function initFileImageEditor(root = document) {
	const wrappers = root.querySelectorAll('[data-lb-file-editor]');
	wrappers.forEach((wrapper) => bindField(wrapper));
}
